/**
 * Story instants are ISO-8601. Milliseconds exist only as call-time math
 * (differences, playback ticks). They are not a second stored clock.
 */

export function storyMs(iso: string): number {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? 0 : t
}

export function isoFromStoryMs(ms: number): string {
  return new Date(ms).toISOString()
}

export function addStoryMs(iso: string, deltaMs: number): string {
  return isoFromStoryMs(storyMs(iso) + deltaMs)
}

/** Seed builders only: story ISO = origin + elapsed ms. Not a persisted field. */
export function storyAt(startsAt: string, elapsedMs: number): string {
  return addStoryMs(startsAt, elapsedMs)
}

export function storyDeltaMs(fromIso: string, toIso: string): number {
  return storyMs(toIso) - storyMs(fromIso)
}

/**
 * In play when playhead >= startsAt (if set) and playhead < endsAt (if set).
 * Omitted bounds are unconstrained.
 */
export function inStoryWindow(playheadIso: string, startsAt?: string, endsAt?: string): boolean {
  const t = storyMs(playheadIso)
  if (startsAt && t < storyMs(startsAt)) return false
  if (endsAt && t >= storyMs(endsAt)) return false
  return true
}

export function compareStory(a: string, b: string): number {
  return storyMs(a) - storyMs(b)
}

function tzOffsetMs(timeZone: string, utcMs: number): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = Object.fromEntries(
    dtf.formatToParts(new Date(utcMs)).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]),
  )
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  )
  return asUtc - utcMs
}

export function civilParts(iso: string, timeZone: string): {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
} {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = Object.fromEntries(
    dtf.formatToParts(new Date(storyMs(iso))).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]),
  )
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  }
}

/** Full ISO for a civil clock on the calendar day of `dayIso`, in `timeZone`. */
export function storyIsoOnDay(
  dayIso: string,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): string {
  const day = civilParts(dayIso, timeZone)
  return storyIsoOnCivilDate(day.year, day.month, day.day, hour, minute, second, timeZone)
}

/** Full ISO for an explicit civil date and clock, in `timeZone` (not the browser zone). */
export function storyIsoOnCivilDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): string {
  let utc = Date.UTC(year, month - 1, day, hour, minute, second)
  for (let i = 0; i < 3; i++) {
    utc = Date.UTC(year, month - 1, day, hour, minute, second) - tzOffsetMs(timeZone, utc)
  }
  return new Date(utc).toISOString()
}

/** Shift a civil calendar day in `timeZone`. DST-safe: not a raw 24h add. */
export function addCivilDays(iso: string, days: number, timeZone: string): string {
  const p = civilParts(iso, timeZone)
  const rolled = new Date(Date.UTC(p.year, p.month - 1, p.day + days))
  return storyIsoOnCivilDate(
    rolled.getUTCFullYear(),
    rolled.getUTCMonth() + 1,
    rolled.getUTCDate(),
    p.hour,
    p.minute,
    p.second,
    timeZone,
  )
}

/**
 * Next civil occurrence of a clock in `timeZone`: today if that time is still
 * ahead, otherwise tomorrow. Used when a story time has no authored date.
 */
export function nextOccurrenceIso(
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
  nowMs = Date.now(),
): string {
  const today = storyIsoOnDay(new Date(nowMs).toISOString(), hour, minute, second, timeZone)
  if (storyMs(today) + 500 >= nowMs) return today
  return addCivilDays(today, 1, timeZone)
}

/** Next occurrence of a clock strictly after `afterIso` (same civil day when it still fits). */
export function nextOccurrenceAfter(
  afterIso: string,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): string {
  const sameDay = storyIsoOnDay(afterIso, hour, minute, second, timeZone)
  if (storyMs(sameDay) > storyMs(afterIso)) return sameDay
  return addCivilDays(sameDay, 1, timeZone)
}

/** 12:00:00 PM today, or tomorrow if noon has already passed in `timeZone`. */
export function fallbackStoryNoon(timeZone: string, nowMs = Date.now()): string {
  return nextOccurrenceIso(12, 0, 0, timeZone, nowMs)
}

/**
 * A new copy keeps the template's clock time, on the next occurrence from now.
 * Blank boards fall back to 12:00:00 PM. The template's historic calendar day is not copied.
 * A session already sitting on that next occurrence is left alone.
 */
export function placeSessionOnNextClock<T extends {
  storyStartTime: string
  storyEndTime?: string
  storyStartDateSet?: boolean
  storyEndDateSet?: boolean
  timezone: string
  timeline: { at: string }[]
  comments: { createdAt: string }[]
  cameraBeats?: { at: string }[]
  objects: {
    startsAt?: string
    endsAt?: string
    addedAt?: string
    comments: { createdAt: string }[]
    keyframes?: { at: string }[]
    radiusKeyframes?: { at: string }[]
    statusKeyframes?: { at: string }[]
    visibilityKeyframes?: { at: string }[]
    windKeyframes?: { at: string }[]
  }[]
}>(session: T, opts?: { blank?: boolean }): T {
  const timeZone = session.timezone || 'America/Chicago'
  const parts = civilParts(session.storyStartTime, timeZone)
  const hour = opts?.blank ? 12 : parts.hour
  const minute = opts?.blank ? 0 : parts.minute
  const second = opts?.blank ? 0 : parts.second
  const next = nextOccurrenceIso(hour, minute, second, timeZone)
  const delta = storyDeltaMs(session.storyStartTime, next)
  if (Math.abs(delta) < 1000) return session
  const shifted = rebaseStorySession(session, delta)
  const copy = { ...shifted }
  delete copy.storyStartDateSet
  if (copy.storyEndTime) delete copy.storyEndDateSet
  return copy
}

function shiftIso(iso: string | undefined, deltaMs: number): string | undefined {
  if (!iso) return iso
  return addStoryMs(iso, deltaMs)
}

/**
 * Add the same delta to every story instant. Actual-audit fields stay put.
 * Does not change clock lifecycle — callers keep the current playhead running or idle.
 */
export function rebaseStorySession<T extends {
  storyStartTime: string
  storyEndTime?: string
  timeline: { at: string }[]
  comments: { createdAt: string }[]
  cameraBeats?: { at: string }[]
  objects: {
    startsAt?: string
    endsAt?: string
    addedAt?: string
    comments: { createdAt: string }[]
    keyframes?: { at: string }[]
    radiusKeyframes?: { at: string }[]
    statusKeyframes?: { at: string }[]
    visibilityKeyframes?: { at: string }[]
    windKeyframes?: { at: string }[]
  }[]
}>(session: T, deltaMs: number): T {
  if (!deltaMs) return session
  const shiftFrames = <F extends { at: string }>(frames: F[] | undefined) =>
    frames?.map((f) => ({ ...f, at: addStoryMs(f.at, deltaMs) }))
  return {
    ...session,
    storyStartTime: addStoryMs(session.storyStartTime, deltaMs),
    storyEndTime: shiftIso(session.storyEndTime, deltaMs),
    timeline: session.timeline.map((ev) => ({ ...ev, at: addStoryMs(ev.at, deltaMs) })),
    comments: session.comments.map((c) => ({ ...c, createdAt: addStoryMs(c.createdAt, deltaMs) })),
    cameraBeats: session.cameraBeats?.map((b) => ({ ...b, at: addStoryMs(b.at, deltaMs) })),
    objects: session.objects.map((obj) => ({
      ...obj,
      startsAt: shiftIso(obj.startsAt, deltaMs),
      endsAt: shiftIso(obj.endsAt, deltaMs),
      addedAt: shiftIso(obj.addedAt, deltaMs),
      comments: obj.comments.map((c) => ({ ...c, createdAt: addStoryMs(c.createdAt, deltaMs) })),
      keyframes: shiftFrames(obj.keyframes),
      radiusKeyframes: shiftFrames(obj.radiusKeyframes),
      statusKeyframes: shiftFrames(obj.statusKeyframes),
      visibilityKeyframes: shiftFrames(obj.visibilityKeyframes),
      windKeyframes: shiftFrames(obj.windKeyframes),
    })),
  }
}

export function formatStoryClock(
  iso: string,
  timeZone: string,
  withSeconds = true,
  withMeridiem = true,
  padHour = false,
): string {
  const parts = civilParts(iso, timeZone)
  const mm = String(parts.minute).padStart(2, '0')
  const ss = String(parts.second).padStart(2, '0')
  // No meridiem → 24h (timeline-friendly). Meridiem → 12h clock.
  if (!withMeridiem) {
    const hour = String(parts.hour).padStart(2, '0')
    return withSeconds ? `${hour}:${mm}:${ss}` : `${hour}:${mm}`
  }
  let h = parts.hour
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  const hour = !padHour ? String(h) : String(h).padStart(2, '0')
  const clock = withSeconds ? `${hour}:${mm}:${ss}` : `${hour}:${mm}`
  return `${clock} ${ampm}`
}

const STORY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** True when two story instants fall on different civil days in `timeZone`. */
export function storyDaysDiffer(aIso: string, bIso: string, timeZone: string): boolean {
  const a = civilParts(aIso, timeZone)
  const b = civilParts(bIso, timeZone)
  return a.year !== b.year || a.month !== b.month || a.day !== b.day
}

/**
 * Minimal IANA list for Settings: US contiguous + common non-contiguous, then UTC.
 * Order is east→west, then Alaska / Hawaii, UTC last. Not a full world list.
 */
export const STUDIO_TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
] as const

/** Display label for an IANA id (underscores → spaces). Value stays the IANA string. */
export function formatTimeZoneLabel(zone: string): string {
  return zone.replace(/_/g, ' ')
}

/** Short zone name for a story instant, such as CST or CDT. */
export function timeZoneAbbrev(iso: string, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date(iso))
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/** Settings and PDFs. `withDate` false prints the clock only; the ISO still has a calendar day. */
export function formatStoryWhen(
  iso: string,
  timeZone: string,
  opts: { withDate: boolean; withSeconds: boolean; padHour?: boolean; showTimeZone?: boolean },
): string {
  const clock = formatStoryClock(iso, timeZone, opts.withSeconds, true, opts.padHour)
  const zone = opts.showTimeZone ? timeZoneAbbrev(iso, timeZone) : ''
  const stamped = zone ? `${clock} ${zone}` : clock
  if (!opts.withDate) return stamped
  const p = civilParts(iso, timeZone)
  const month = STORY_MONTHS[p.month - 1] ?? ''
  return `${month} ${p.day}, ${p.year} ${stamped}`
}

function monthNumber(name: string): number | null {
  const key = name.toLowerCase().slice(0, 3)
  const i = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(key)
  return i >= 0 ? i + 1 : null
}

/** 24h clock. Hours 1–12 need AM/PM. Omitted seconds are 0. */
function parseCivilClock(
  hourRaw: string,
  minuteRaw: string,
  secondRaw: string | undefined,
  meridiem: string | undefined,
  hour24 = false,
): { hour: number; minute: number; second: number } | null {
  let hour = Number(hourRaw)
  const minute = Number(minuteRaw)
  const second = secondRaw != null ? Number(secondRaw) : 0
  if (minute > 59 || second > 59) return null
  const ap = meridiem?.toLowerCase()
  if (ap) {
    if (hour < 1 || hour > 12) return null
    if (ap === 'pm' && hour < 12) hour += 12
    if (ap === 'am' && hour === 12) hour = 0
  } else if (hour24) {
    if (hour > 23) return null
  } else if (hour > 23 || (hour >= 1 && hour <= 12)) {
    return null
  }
  return { hour, minute, second }
}

export type StoryWhenValue = { iso: string; dateSet: boolean }

/**
 * Accept a clock, a date, or both.
 * Time only → next occurrence (today or tomorrow) in `timeZone`, date not shown.
 * Date only → 12:00:00 PM on that date.
 * Empty text → null. Unparseable → undefined.
 */
export function parseStoryWhen(
  text: string,
  timeZone: string,
  opts?: { afterIso?: string },
): StoryWhenValue | null | undefined {
  const raw = text.trim().replace(/\s+/g, ' ')
  if (!raw) return null

  const place = (hour: number, minute: number, second: number, dateSet: boolean, y?: number, m?: number, d?: number) => {
    if (y != null && m != null && d != null) {
      return { iso: storyIsoOnCivilDate(y, m, d, hour, minute, second, timeZone), dateSet }
    }
    const iso = opts?.afterIso
      ? nextOccurrenceAfter(opts.afterIso, hour, minute, second, timeZone)
      : nextOccurrenceIso(hour, minute, second, timeZone)
    return { iso, dateSet }
  }

  const dated = raw.match(
    /^(?:(\d{4})-(\d{1,2})-(\d{1,2})|([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})|(\d{1,2})\/(\d{1,2})\/(\d{4}))(?:[T\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?)?$/i,
  )
  if (dated) {
    let year: number
    let month: number
    let day: number
    if (dated[1]) {
      year = Number(dated[1])
      month = Number(dated[2])
      day = Number(dated[3])
    } else if (dated[4]) {
      const mon = monthNumber(dated[4])
      if (mon == null) return undefined
      month = mon
      day = Number(dated[5])
      year = Number(dated[6])
    } else {
      month = Number(dated[7])
      day = Number(dated[8])
      year = Number(dated[9])
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return undefined
    if (!dated[10]) return place(12, 0, 0, true, year, month, day)
    const clock = parseCivilClock(dated[10], dated[11], dated[12], dated[13], Boolean(dated[1]))
    if (!clock) return undefined
    return place(clock.hour, clock.minute, clock.second, true, year, month, day)
  }

  const clockOnly = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i)
  if (!clockOnly) return undefined
  const clock = parseCivilClock(clockOnly[1], clockOnly[2], clockOnly[3], clockOnly[4])
  if (!clock) return undefined
  return place(clock.hour, clock.minute, clock.second, false)
}

/** Value for `<input type="datetime-local">`, as civil time in `timeZone`. */
export function storyWhenLocalInputValue(
  iso: string,
  timeZone: string,
  withSeconds = true,
): string {
  const p = civilParts(iso, timeZone)
  const pad = (n: number) => String(n).padStart(2, '0')
  const hm = `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
  // Minute-only values omit seconds so browsers don't surface a seconds spinner.
  return withSeconds ? `${hm}:${pad(p.second)}` : hm
}
